import { useCallback, useEffect, useMemo } from 'react';

import type { PharmacyProductInventory } from '@montugroup/circuit-shared/types/pharmacy';

import useGetInventoryProducts from '@/hooks/useGetInventoryProducts';
import { useGetPharmacistDetails } from '@/hooks/useGetPharmacistDetails';
import type { Stocktake, StocktakeEntryUpdate } from './useStoredStocktake';
import { useStoredStocktake } from './useStoredStocktake';

type StocktakeStatus = 'loading' | 'error' | 'notStarted' | 'inProgress';

export type StocktakeItem = Readonly<{
  productId: number;
  name: string;
  sku: string;
  expectedStock: number;
  countedStock?: number;
  discrepancy?: number;
  reason?: string;
}>;

export type AddableProduct = Readonly<{ productId: number; name: string; sku: string; expectedStock: number }>;

const buildStocktakeItems = (
  products: readonly PharmacyProductInventory[],
  stocktake: Stocktake | null,
): StocktakeItem[] => {
  if (stocktake === null) {
    return [];
  }
  return products.flatMap((product): StocktakeItem[] => {
    const entry = stocktake.entries[product.id];
    if (!entry) {
      return [];
    }
    const { expectedStock, countedStock, reason } = entry;
    return [
      {
        productId: product.id,
        name: product.name,
        sku: product.sku,
        expectedStock,
        countedStock,
        discrepancy: countedStock === undefined ? undefined : countedStock - expectedStock,
        reason,
      },
    ];
  });
};

const buildAddableProducts = (
  products: readonly PharmacyProductInventory[],
  stocktake: Stocktake | null,
): AddableProduct[] => {
  if (stocktake === null) {
    return [];
  }
  return products
    .filter((product) => !stocktake.entries[product.id])
    .map((product) => ({ productId: product.id, name: product.name, sku: product.sku, expectedStock: product.stock }));
};

const stocktakeStatus = (isLoading: boolean, isError: boolean, stocktake: Stocktake | null): StocktakeStatus => {
  if (isLoading) {
    return 'loading';
  }
  if (isError) {
    return 'error';
  }
  return stocktake === null ? 'notStarted' : 'inProgress';
};

type UseStocktakeResult = {
  status: StocktakeStatus;
  stocktakeItems: StocktakeItem[];
  addableProducts: AddableProduct[];
  retry: () => void;
  startNewStocktake: () => void;
  addStocktakeItems: (productIds: readonly number[]) => void;
  setStocktakeItemCount: (productId: number, countedStock: number | undefined) => void;
  setStocktakeItemReason: (productId: number, reason: string | undefined) => void;
  clearStocktake: () => void;
};

/**
 * Loads the signed-in pharmacy's products and its in-progress stocktake, joined into stocktake items.
 * @returns The load `status`, the `stocktakeItems` in product order, the `addableProducts` not in the stocktake, and actions to retry a failed load and start, add to, update and clear the stocktake.
 */
export function useStocktake(): UseStocktakeResult {
  const {
    data: pharmacistDetails,
    isError: isPharmacyError,
    isLoading: isPharmacyLoading,
    refetch: refetchPharmacistDetails,
  } = useGetPharmacistDetails();
  const pharmacyId = pharmacistDetails?.pharmacy_id ?? null;

  const {
    products,
    isLoading: isProductsLoading,
    isError: isProductsError,
    refetch: refetchProducts,
  } = useGetInventoryProducts(pharmacyId);

  const { stocktake, startStocktake, addEntries, updateEntries, clearStocktake } = useStoredStocktake(pharmacyId);

  // Handles where stock changes mid-draft
  useEffect(() => {
    if (stocktake === null) {
      return;
    }

    // Case where the draft did not have the product and new product was added with stock
    addEntries(
      products
        .filter(({ id, stock }) => !stocktake.entries[id] && stock > 0)
        .map(({ id, stock }) => ({ productId: id, expectedStock: stock })),
    );

    updateEntries(
      products.flatMap(({ id, stock }): StocktakeEntryUpdate[] => {
        const entry = stocktake.entries[id];
        if (!entry) {
          return [];
        }
        const updates: StocktakeEntryUpdate[] = [];

        // Handle where the stock level of an entry has changed
        if (entry.expectedStock !== stock) {
          updates.push({ productId: id, expectedStock: stock });
        }

        // Handles case where initially there was a discrepancy, but the stock change means no more discrepancy
        // Clear reason once the count matches the stock
        if (entry.reason !== undefined && entry.countedStock === stock) {
          updates.push({ productId: id, reason: undefined });
        }
        return updates;
      }),
    );
  }, [addEntries, products, stocktake, updateEntries]);

  const stocktakeItems = useMemo(() => buildStocktakeItems(products, stocktake), [products, stocktake]);
  const addableProducts = useMemo(() => buildAddableProducts(products, stocktake), [products, stocktake]);

  const retry = useCallback(() => {
    if (isPharmacyError) {
      refetchPharmacistDetails();
    }
    if (isProductsError) {
      refetchProducts();
    }
  }, [isPharmacyError, isProductsError, refetchPharmacistDetails, refetchProducts]);

  const startNewStocktake = useCallback(() => {
    startStocktake(
      products
        // Starting with products with stock
        .filter((product) => product.stock > 0)
        .map((product) => ({ productId: product.id, expectedStock: product.stock })),
    );
  }, [products, startStocktake]);

  const addStocktakeItems = useCallback(
    (productIds: readonly number[]) => {
      addEntries(
        products
          .filter((product) => productIds.includes(product.id))
          .map((product) => ({ productId: product.id, expectedStock: product.stock })),
      );
    },
    [addEntries, products],
  );

  const setStocktakeItemCount = useCallback(
    (productId: number, countedStock: number | undefined) => {
      updateEntries([
        countedStock === undefined ? { productId, countedStock, reason: undefined } : { productId, countedStock },
      ]);
    },
    [updateEntries],
  );

  const setStocktakeItemReason = useCallback(
    (productId: number, reason: string | undefined) => {
      updateEntries([{ productId, reason }]);
    },
    [updateEntries],
  );

  const status = stocktakeStatus(isPharmacyLoading || isProductsLoading, isPharmacyError || isProductsError, stocktake);

  return {
    status,
    stocktakeItems,
    addableProducts,
    retry,
    startNewStocktake,
    addStocktakeItems,
    setStocktakeItemCount,
    setStocktakeItemReason,
    clearStocktake,
  };
}
